import { useEffect, useId, useState, type FormEvent, type ReactElement } from 'react';
import { useNavigate } from 'react-router';

import { ModalShell } from '@/components/ui/ModalShell';
import { useToast } from '@/components/ui/toast';
import { useActiveCompany } from '@/lib/active-company-context';
import { useBackState } from '@/lib/back-nav';
import { useCatalog } from '@/lib/quality-catalogs';

import { EMPTY_DOCUMENT_FORM, useDocumentForm } from '../use-document-form';
import { useRegisterDocument } from '../use-document-registration';
import { DocumentFields } from './DocumentFields';

// Alta de documento (QA F1 #6/#15, G3-2/G3-3): Calidad elige tipo y proceso, el servidor
// propone el código según 04-GC-P08 (editable) y el documento nace reservado, con su
// dueño de proceso si se eligió. Al reservar se abre su ficha, donde sigue el trabajo.

export function NewDocumentModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): ReactElement | null {
  const formId = useId();
  const { activeCompanyId } = useActiveCompany();
  const scope = { companyId: activeCompanyId, enabled: open };
  const types = useCatalog('document-types', scope);
  const areas = useCatalog('process-areas', scope);
  const register = useRegisterDocument();
  const state = useDocumentForm(EMPTY_DOCUMENT_FORM);
  const { showToast } = useToast();
  const navigate = useNavigate();
  // Solo se abre desde la Lista Maestra: la ficha nueva vuelve ahí, con sus filtros.
  const fromList = useBackState('Lista Maestra');
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { reset } = state;

  useEffect(() => {
    if (open) {
      reset(EMPTY_DOCUMENT_FORM);
      setFormError(null);
    }
  }, [open, reset]);

  const pending = busy || register.isPending;

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const valid = state.check();
    if (!state.complete || pending || !valid) {
      return;
    }
    setFormError(null);
    setBusy(true);
    try {
      const created = await register.mutateAsync(await state.payload());
      showToast(`Código ${created.code} reservado`);
      onClose();
      void navigate(`/documentos/${created.id}`, { state: fromList });
    } catch (err) {
      setFormError(state.rejection(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell
      open={open}
      title="Nuevo documento"
      wide
      busy={pending}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn mut sm" onClick={onClose} disabled={pending}>
            Cancelar
          </button>
          <button
            type="submit"
            form={formId}
            className="btn sm"
            disabled={!state.complete || pending}
          >
            {pending ? 'Reservando…' : 'Reservar código'}
          </button>
        </>
      }
    >
      <p className="modal-body">
        El documento queda reservado con este código y nombre; nadie más podrá usar el código.
      </p>
      <form id={formId} className="form-grid" noValidate onSubmit={(e) => void submit(e)}>
        <DocumentFields state={state} types={types} areas={areas} />
        {formError && (
          <p className="fld-error" role="alert">
            {formError}
          </p>
        )}
      </form>
    </ModalShell>
  );
}
